/**
 * FlowTagChips — ⚡ ranking-tag chips for a Flow Optimizer preview/overlay row.
 *
 * Reads the tags already computed by FlowTagsProvider (in scope under the
 * optimizer preview overlay) from the SAME preview fetch — no extra request.
 * Renders nothing when there are no tags or no provider, so it's safe to drop
 * into any path/lander/offer row.
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';
import type { FlowLayer } from '@trafficpulse/shared';

import { cn } from '@/lib/utils';

import { useFlowTagsFor } from './flow-tags-context';

export interface FlowTagChipsProps {
  layer: FlowLayer;
  itemId: string;
  pathId?: string;
  className?: string;
}

export const FlowTagChips: FC<FlowTagChipsProps> = ({
  layer,
  itemId,
  pathId,
  className,
}) => {
  const { tags, colorOf, emojiOf } = useFlowTagsFor(layer, itemId, pathId);
  if (!tags.length) return null;
  return (
    <span
      className={cn('inline-flex flex-wrap items-center gap-0.5 align-middle', className)}
      title="Flow optimisation tags (raw-score ranking)"
    >
      {tags.map((tag) => {
        const color = colorOf(tag);
        return (
          <span
            key={tag}
            className="inline-flex items-center gap-0.5 rounded-full border px-1 text-[9px] font-semibold leading-none"
            style={
              color
                ? { backgroundColor: `${color}22`, color, borderColor: `${color}55` }
                : undefined
            }
          >
            <span aria-hidden>{emojiOf(tag) || '⚡'}</span>
            {tag}
          </span>
        );
      })}
    </span>
  );
};

export default FlowTagChips;
